import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../contexts/I18nContext';

const Home: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useI18n();

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: 'spring',
        stiffness: 100,
        damping: 15,
      },
    },
  };

  const buttonVariants = {
    hidden: { scale: 0.8, opacity: 0 },
    visible: {
      scale: 1,
      opacity: 1,
      transition: {
        type: 'spring',
        stiffness: 200,
        damping: 15,
      },
    },
    hover: {
      scale: 1.05,
      transition: {
        type: 'spring',
        stiffness: 400,
        damping: 10,
      },
    },
    tap: {
      scale: 0.95,
    },
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 pt-20 pb-24">
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="max-w-4xl mx-auto text-center"
      >
        {/* Main Title */}
        <motion.h1
          variants={itemVariants}
          className="text-4xl md:text-6xl lg:text-7xl font-contrail font-bold text-gray-900 dark:text-gray-100 mb-6"
        >
          {t('mainTitle')}
        </motion.h1>

        {/* Subtitle */}
        <motion.p
          variants={itemVariants}
          className="text-lg md:text-xl text-gray-600 dark:text-gray-300 mb-12 max-w-3xl mx-auto leading-relaxed"
        >
          {t('mainSubtitle')}
        </motion.p>

        {/* Main Buttons */}
        <motion.div
          variants={itemVariants}
          className="flex flex-col gap-4 mb-12 max-w-md mx-auto"
        >
          <motion.button
            variants={buttonVariants}
            whileHover="hover"
            whileTap="tap"
            onClick={() => navigate('/my-file')}
            className="btn-primary text-lg py-4 px-8"
          >
            {t('myFile')}
          </motion.button>

          <motion.button
            variants={buttonVariants}
            whileHover="hover"
            whileTap="tap"
            onClick={() => navigate('/generate')}
            className="btn-primary text-lg py-4 px-8"
          >
            {t('generateWithAI')}
          </motion.button>

          <motion.button
            variants={buttonVariants}
            whileHover="hover"
            whileTap="tap"
            onClick={() => navigate('/catalog')}
            className="btn-primary text-lg py-4 px-8"
          >
            {t('manageMyCatalog')}
          </motion.button>

          <motion.button
            variants={buttonVariants}
            whileHover="hover"
            whileTap="tap"
            onClick={() => navigate('/publish')}
            className="btn-primary text-lg py-4 px-8"
          >
            {t('publishWithAI')}
          </motion.button>

          <motion.button
            variants={buttonVariants}
            whileHover="hover"
            whileTap="tap"
            onClick={() => navigate('/settings')}
            className="btn-secondary text-lg py-4 px-8"
          >
            {t('settings')}
          </motion.button>
        </motion.div>

        {/* Demo Section */}
        <motion.div
          variants={itemVariants}
          className="space-y-4"
        >
          <p className="text-gray-600 dark:text-gray-300 text-lg">
            {t('firstTime')}
          </p>
          
          <motion.button
            variants={buttonVariants}
            whileHover="hover"
            whileTap="tap"
            className="btn-secondary text-lg py-4 px-8"
          >
            {t('watchDemo')}
          </motion.button>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default Home;
